import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Copy, Check, ChevronDown, ChevronUp, Play, Loader2,
  Lock, Globe, Zap, FileText, Key, BookOpen, Terminal
} from "lucide-react";
import { useAppConfig } from "@/contexts/AppConfigContext";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────────

function useCopy(timeout = 2000) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((text) => {
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    });
    setCopied(true);
    setTimeout(() => setCopied(false), timeout);
  }, [timeout]);
  return [copied, copy];
}

// ── Method Badge ──────────────────────────────────────────────────────────────

const METHOD_STYLES = {
  POST:   "bg-[#006080] text-white",
  GET:    "bg-slate-600 text-white",
  PUT:    "bg-amber-600 text-white",
  DELETE: "bg-red-600 text-white",
  PATCH:  "bg-purple-600 text-white",
};

function MethodBadge({ method }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase ${METHOD_STYLES[method] || "bg-slate-500 text-white"}`}>
      {method}
    </span>
  );
}

// ── Code Block ────────────────────────────────────────────────────────────────

function CodeBlock({ code, language = "bash" }) {
  const [copied, copy] = useCopy();
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-950 overflow-hidden text-sm">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-800">
        <span className="text-[11px] font-mono font-medium text-slate-400 uppercase tracking-wider">{language}</span>
        <button
          onClick={() => copy(code)}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors px-2 py-0.5 rounded hover:bg-slate-700"
        >
          {copied ? <><Check size={11} className="text-emerald-400" /><span className="text-emerald-400">Copied</span></> : <><Copy size={11} /><span>Copy</span></>}
        </button>
      </div>
      <pre className="p-4 text-xs font-mono text-slate-200 overflow-x-auto whitespace-pre leading-relaxed max-h-80 overflow-y-auto">{code}</pre>
    </div>
  );
}

// ── Code Tabs ─────────────────────────────────────────────────────────────────

const TAB_LABELS = { curl: "cURL", python: "Python", javascript: "JavaScript" };

function CodeTabs({ examples }) {
  const [active, setActive] = useState(Object.keys(examples)[0] || "curl");
  return (
    <div>
      <div className="flex gap-0 border-b border-slate-200 mb-0">
        {Object.keys(examples).map(tab => (
          <button
            key={tab}
            onClick={() => setActive(tab)}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
              active === tab
                ? "border-[#006080] text-[#006080]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {TAB_LABELS[tab] || tab}
          </button>
        ))}
      </div>
      <div className="mt-3">
        <CodeBlock code={examples[active]} language={active === "javascript" ? "javascript" : active === "python" ? "python" : "bash"} />
      </div>
    </div>
  );
}

// ── Param Table ───────────────────────────────────────────────────────────────

function ParamTable({ params }) {
  if (!params || params.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-3 py-2 font-semibold text-slate-600 w-1/4">Name</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600 w-1/5">Type</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600 w-16">Required</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map((p, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
              <td className="px-3 py-2 font-mono text-[#006080] font-medium">{p.name}</td>
              <td className="px-3 py-2 text-slate-500 font-mono">{p.type}</td>
              <td className="px-3 py-2">
                {p.required
                  ? <span className="text-red-500 font-bold">yes</span>
                  : <span className="text-slate-400">no</span>}
              </td>
              <td className="px-3 py-2 text-slate-600">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Try It Form ───────────────────────────────────────────────────────────────

function TryItForm({ tryIt }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(() => Object.fromEntries(tryIt.fields.map(f => [f.name, f.default || ""])));
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [statusCode, setStatusCode] = useState(null);

  const set = (name, value) => setValues(v => ({ ...v, [name]: value }));

  const execute = async () => {
    setLoading(true);
    setResponse(null);
    setStatusCode(null);
    try {
      const result = await tryIt.execute(values);
      setStatusCode(result.status);
      setResponse(JSON.stringify(result.data, null, 2));
    } catch (err) {
      setStatusCode(err.status || 500);
      setResponse(JSON.stringify(err.data || { error: err.message || "Request failed" }, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const statusColor = statusCode >= 200 && statusCode < 300 ? "text-emerald-600" : "text-red-500";

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700"
      >
        <div className="flex items-center gap-2">
          <Terminal size={14} className="text-[#006080]" />
          <span>Try it out</span>
        </div>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div className="p-4 space-y-3 border-t border-slate-200 bg-white">
          {tryIt.fields.map(field => (
            <div key={field.name}>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#006080] focus:border-[#006080] min-h-20 resize-y bg-white"
                  placeholder={field.placeholder || ""}
                  value={values[field.name]}
                  onChange={e => set(field.name, e.target.value)}
                />
              ) : (
                <input
                  type={field.inputType || "text"}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#006080] focus:border-[#006080] bg-white"
                  placeholder={field.placeholder || ""}
                  value={values[field.name]}
                  onChange={e => set(field.name, e.target.value)}
                />
              )}
              {field.hint && <p className="text-[10px] text-slate-400 mt-0.5">{field.hint}</p>}
            </div>
          ))}

          <button
            onClick={execute}
            disabled={loading}
            className="flex items-center gap-2 h-8 px-4 bg-[#006080] hover:bg-[#004B66] disabled:opacity-50 text-white text-xs font-semibold rounded-md transition-colors"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {loading ? "Executing..." : "Execute"}
          </button>

          {response !== null && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-semibold text-slate-600">Response</span>
                {statusCode && (
                  <span className={`text-xs font-bold font-mono ${statusColor}`}>{statusCode}</span>
                )}
              </div>
              <CodeBlock code={response} language="json" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Endpoint Card ─────────────────────────────────────────────────────────────

function EndpointCard({ ep }) {
  return (
    <div id={ep.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden scroll-mt-6">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
        <MethodBadge method={ep.method} />
        <code className="text-sm font-mono font-medium text-slate-800 flex-1">{ep.path}</code>
        {ep.auth && (
          <span className="flex items-center gap-1 text-[10px] font-medium text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">
            <Lock size={9} /> {ep.auth}
          </span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Description */}
        <div>
          <h4 className="text-sm font-semibold text-slate-900 mb-1">{ep.title}</h4>
          <p className="text-sm text-slate-500 leading-relaxed">{ep.description}</p>
        </div>

        {/* Headers */}
        {ep.headers && ep.headers.length > 0 && (
          <div>
            <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Headers</h5>
            <ParamTable params={ep.headers} />
          </div>
        )}

        {/* Path params */}
        {ep.pathParams && ep.pathParams.length > 0 && (
          <div>
            <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Path Parameters</h5>
            <ParamTable params={ep.pathParams} />
          </div>
        )}

        {/* Body params */}
        {ep.bodyParams && ep.bodyParams.length > 0 && (
          <div>
            <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Request Body</h5>
            <ParamTable params={ep.bodyParams} />
          </div>
        )}

        {/* Response example */}
        {ep.responseExample && (
          <div>
            <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Response</h5>
            <CodeBlock code={JSON.stringify(ep.responseExample, null, 2)} language="json" />
          </div>
        )}

        {/* Code examples */}
        {ep.codeExamples && (
          <div>
            <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Code Examples</h5>
            <CodeTabs examples={ep.codeExamples} />
          </div>
        )}

        {/* Try it out */}
        {ep.tryIt && <TryItForm tryIt={ep.tryIt} />}
      </div>
    </div>
  );
}

// ── API Data ──────────────────────────────────────────────────────────────────

function buildEndpoints(baseUrl) {
  return [
    // ── Authentication ──────────────────────────────────────────────────────
    {
      sectionId: "auth",
      id: "ep-login",
      method: "POST",
      path: "/api/auth/login",
      title: "Obtain JWT Token",
      description: "Authenticate with your email and password to receive a Bearer token. Include this token in the Authorization header for all authenticated requests.",
      headers: [
        { name: "Content-Type", type: "string", required: true, description: "Must be application/json" },
      ],
      bodyParams: [
        { name: "email", type: "string", required: true, description: "Registered email address" },
        { name: "password", type: "string", required: true, description: "Account password" },
      ],
      responseExample: {
        access_token: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
        token_type: "bearer",
        user: { id: "uuid", email: "you@example.com", role: "company_admin" }
      },
      codeExamples: {
        curl: `curl -X POST "${baseUrl}/api/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "you@example.com",
    "password": "yourpassword"
  }'`,
        python: `import requests

response = requests.post(
    "${baseUrl}/api/auth/login",
    json={"email": "you@example.com", "password": "yourpassword"}
)

data = response.json()
token = data["access_token"]
print(f"Token: {token}")`,
        javascript: `const response = await fetch("${baseUrl}/api/auth/login", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    email: "you@example.com",
    password: "yourpassword"
  })
});

const { access_token } = await response.json();
console.log("Token:", access_token);`,
      },
      tryIt: {
        fields: [
          { name: "email", label: "Email", required: true, placeholder: "you@example.com" },
          { name: "password", label: "Password", required: true, inputType: "password", placeholder: "your password" },
        ],
        execute: async (values) => {
          const res = await fetch(`${baseUrl}/api/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: values.email, password: values.password }),
          });
          const data = await res.json();
          if (!res.ok) throw { status: res.status, data };
          return { status: res.status, data };
        },
      },
    },

    // ── Direct Ingest ────────────────────────────────────────────────────────
    {
      sectionId: "ingest",
      id: "ep-ingest",
      method: "POST",
      path: "/api/v1/ingest",
      auth: "X-Api-Key",
      title: "Ingest a Document",
      description: "Submit a document for OCR processing and field extraction using your company API key. You can provide the document as a base64-encoded string or a publicly accessible URL.",
      headers: [
        { name: "X-Api-Key", type: "string", required: true, description: "Your company API key (generate in API Keys section)" },
        { name: "Content-Type", type: "string", required: true, description: "application/json" },
      ],
      bodyParams: [
        { name: "filename", type: "string", required: true, description: "Original filename including extension (e.g. invoice.pdf)" },
        { name: "file_base64", type: "string", required: false, description: "Base64-encoded file content. Provide this OR file_url." },
        { name: "file_url", type: "string", required: false, description: "Publicly accessible URL of the file. Provide this OR file_base64." },
        { name: "mime_type", type: "string", required: false, description: "MIME type: application/pdf, image/jpeg, image/png, image/webp (default: application/pdf)" },
        { name: "schema_id", type: "string", required: false, description: "Schema ID to extract fields from. If omitted, auto-detection is attempted." },
      ],
      responseExample: {
        document_id: "3f1e9b2c-...",
        status: "review",
        pages_processed: 2,
        extracted_fields: {
          invoice_number: "INV-2024-001",
          total_amount: "1,250.00",
          vendor_name: "Acme Corp"
        },
        ocr_text: "INVOICE\nDate: January 15, 2024\n..."
      },
      codeExamples: {
        curl: `curl -X POST "${baseUrl}/api/v1/ingest" \\
  -H "X-Api-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "filename": "invoice.pdf",
    "file_url": "https://example.com/invoice.pdf",
    "mime_type": "application/pdf",
    "schema_id": "your_schema_id"
  }'`,
        python: `import requests

response = requests.post(
    "${baseUrl}/api/v1/ingest",
    headers={"X-Api-Key": "your_api_key_here"},
    json={
        "filename": "invoice.pdf",
        "file_url": "https://example.com/invoice.pdf",
        "mime_type": "application/pdf",
        "schema_id": "your_schema_id"  # optional
    }
)

result = response.json()
print(f"Document ID: {result['document_id']}")
print(f"Extracted fields: {result['extracted_fields']}")`,
        javascript: `const response = await fetch("${baseUrl}/api/v1/ingest", {
  method: "POST",
  headers: {
    "X-Api-Key": "your_api_key_here",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    filename: "invoice.pdf",
    file_url: "https://example.com/invoice.pdf",
    mime_type: "application/pdf",
    schema_id: "your_schema_id"  // optional
  })
});

const result = await response.json();
console.log("Document ID:", result.document_id);
console.log("Fields:", result.extracted_fields);`,
      },
      tryIt: {
        fields: [
          { name: "api_key", label: "API Key (X-Api-Key)", required: true, placeholder: "Paste your company API key" },
          { name: "file_url", label: "File URL", required: false, placeholder: "https://example.com/document.pdf", hint: "Public URL to a PDF, JPEG, PNG, or WebP file" },
          { name: "schema_id", label: "Schema ID (optional)", required: false, placeholder: "Leave blank for auto-detection" },
          { name: "mime_type", label: "MIME Type", required: false, placeholder: "application/pdf", default: "application/pdf" },
        ],
        execute: async (values) => {
          const body = {
            filename: values.file_url ? values.file_url.split("/").pop() || "document.pdf" : "document.pdf",
            file_url: values.file_url || undefined,
            mime_type: values.mime_type || "application/pdf",
          };
          if (values.schema_id) body.schema_id = values.schema_id;
          const res = await fetch(`${baseUrl}/api/v1/ingest`, {
            method: "POST",
            headers: { "X-Api-Key": values.api_key, "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (!res.ok) throw { status: res.status, data };
          return { status: res.status, data };
        },
      },
    },

    // ── Pipeline Ingest ──────────────────────────────────────────────────────
    {
      sectionId: "pipeline",
      id: "ep-pipeline-ingest",
      method: "POST",
      path: "/api/pipelines/{pipeline_id}/ingest",
      auth: "X-Pipeline-Key",
      title: "Pipeline Document Extraction",
      description: "Submit a document to a Pipeline. The document is evaluated against all schemas in the pipeline. Each schema matching the min_fields_matched threshold is included in the response. Ideal for multi-schema documents or when schema type is unknown upfront.",
      headers: [
        { name: "X-Pipeline-Key", type: "string", required: true, description: "Pipeline API key (scoped to a specific pipeline)" },
        { name: "Content-Type", type: "string", required: true, description: "application/json" },
      ],
      pathParams: [
        { name: "pipeline_id", type: "string", required: true, description: "The unique ID of the pipeline to run" },
      ],
      bodyParams: [
        { name: "filename", type: "string", required: false, description: "Original filename (default: document.pdf)" },
        { name: "file_base64", type: "string", required: false, description: "Base64-encoded file content. Provide this OR file_url." },
        { name: "file_url", type: "string", required: false, description: "Publicly accessible file URL. Provide this OR file_base64." },
        { name: "mime_type", type: "string", required: false, description: "application/pdf, image/jpeg, image/png, or image/webp (default: application/pdf)" },
      ],
      responseExample: {
        document_id: "8a3f2e1d-...",
        pipeline_id: "pl_xxxxxxxx",
        pipeline_name: "Invoice & Receipt Classifier",
        pages_processed: 1,
        schemas_evaluated: [
          {
            schema_id: "sch_invoice",
            schema_name: "Invoice Schema",
            matched: true,
            match_reason: "3 field(s) extracted",
            extracted_fields: {
              invoice_number: "INV-001",
              amount: "850.00",
              vendor: "Acme Corp"
            }
          },
          {
            schema_id: "sch_receipt",
            schema_name: "Receipt Schema",
            matched: false,
            match_reason: "Field threshold not met: 0/2 field(s) extracted",
            extracted_fields: {}
          }
        ]
      },
      codeExamples: {
        curl: `curl -X POST "${baseUrl}/api/pipelines/{pipeline_id}/ingest" \\
  -H "X-Pipeline-Key: pk_your_pipeline_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "filename": "document.pdf",
    "file_url": "https://example.com/document.pdf",
    "mime_type": "application/pdf"
  }'`,
        python: `import requests

PIPELINE_ID = "your_pipeline_id"
PIPELINE_KEY = "pk_your_key_here"

response = requests.post(
    f"${baseUrl}/api/pipelines/{'{PIPELINE_ID}'}/ingest",
    headers={"X-Pipeline-Key": PIPELINE_KEY},
    json={
        "filename": "document.pdf",
        "file_url": "https://example.com/document.pdf",
        "mime_type": "application/pdf"
    }
)

result = response.json()
for schema in result["schemas_evaluated"]:
    if schema["matched"]:
        print(f"✓ {schema['schema_name']}: {schema['extracted_fields']}")
    else:
        print(f"✗ {schema['schema_name']}: {schema['match_reason']}")`,
        javascript: `const PIPELINE_ID = "your_pipeline_id";
const PIPELINE_KEY = "pk_your_key_here";

const response = await fetch(
  \`${baseUrl}/api/pipelines/\${PIPELINE_ID}/ingest\`,
  {
    method: "POST",
    headers: {
      "X-Pipeline-Key": PIPELINE_KEY,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      filename: "document.pdf",
      file_url: "https://example.com/document.pdf",
      mime_type: "application/pdf"
    })
  }
);

const result = await response.json();
const matched = result.schemas_evaluated.filter(s => s.matched);
console.log(\`\${matched.length} schema(s) matched\`);`,
      },
      tryIt: {
        fields: [
          { name: "pipeline_id", label: "Pipeline ID", required: true, placeholder: "Find in Pipelines page" },
          { name: "pipeline_key", label: "Pipeline Key (X-Pipeline-Key)", required: true, placeholder: "pk_xxxxxxxx..." },
          { name: "file_url", label: "File URL", required: true, placeholder: "https://example.com/document.pdf" },
          { name: "mime_type", label: "MIME Type", required: false, placeholder: "application/pdf", default: "application/pdf" },
        ],
        execute: async (values) => {
          const res = await fetch(`${baseUrl}/api/pipelines/${values.pipeline_id}/ingest`, {
            method: "POST",
            headers: { "X-Pipeline-Key": values.pipeline_key, "Content-Type": "application/json" },
            body: JSON.stringify({
              filename: values.file_url.split("/").pop() || "document.pdf",
              file_url: values.file_url,
              mime_type: values.mime_type || "application/pdf",
            }),
          });
          const data = await res.json();
          if (!res.ok) throw { status: res.status, data };
          return { status: res.status, data };
        },
      },
    },

    // ── Pipeline Management ──────────────────────────────────────────────────
    {
      sectionId: "pipeline",
      id: "ep-list-pipelines",
      method: "GET",
      path: "/api/pipelines",
      auth: "Bearer JWT",
      title: "List Pipelines",
      description: "Retrieve all pipelines for your company. Superadmins can see all pipelines across companies.",
      headers: [
        { name: "Authorization", type: "string", required: true, description: "Bearer {jwt_token}" },
      ],
      responseExample: [
        {
          id: "pl_abc123",
          name: "Invoice & Receipt Classifier",
          description: "Handles invoice and receipt documents",
          schema_ids: ["sch_invoice", "sch_receipt"],
          min_fields_matched: 2,
          is_active: true,
          created_at: "2024-01-15T10:30:00Z"
        }
      ],
      codeExamples: {
        curl: `curl -X GET "${baseUrl}/api/pipelines" \\
  -H "Authorization: Bearer your_jwt_token"`,
        python: `import requests

headers = {"Authorization": "Bearer your_jwt_token"}
response = requests.get("${baseUrl}/api/pipelines", headers=headers)
pipelines = response.json()
for pipeline in pipelines:
    print(f"{pipeline['name']} — {len(pipeline['schema_ids'])} schemas")`,
        javascript: `const response = await fetch("${baseUrl}/api/pipelines", {
  headers: { "Authorization": \`Bearer \${token}\` }
});
const pipelines = await response.json();`,
      },
    },

    {
      sectionId: "pipeline",
      id: "ep-create-pipeline",
      method: "POST",
      path: "/api/pipelines",
      auth: "Bearer JWT",
      title: "Create Pipeline",
      description: "Create a new pipeline by grouping schemas together. Set min_fields_matched to control how many fields must be extracted for a schema to be considered a match.",
      headers: [
        { name: "Authorization", type: "string", required: true, description: "Bearer {jwt_token}" },
      ],
      bodyParams: [
        { name: "name", type: "string", required: true, description: "Pipeline display name" },
        { name: "description", type: "string", required: false, description: "Optional description" },
        { name: "schema_ids", type: "array[string]", required: false, description: "List of schema IDs to include in the pipeline" },
        { name: "min_fields_matched", type: "integer", required: false, description: "Minimum fields a schema must extract to count as matched (default: 1)" },
        { name: "webhook_id", type: "string", required: false, description: "Webhook to fire when extraction completes" },
        { name: "is_active", type: "boolean", required: false, description: "Whether the pipeline is active (default: true)" },
      ],
      codeExamples: {
        curl: `curl -X POST "${baseUrl}/api/pipelines" \\
  -H "Authorization: Bearer your_jwt_token" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Invoice Classifier",
    "description": "Handles invoices and receipts",
    "schema_ids": ["schema_id_1", "schema_id_2"],
    "min_fields_matched": 2,
    "is_active": true
  }'`,
        python: `import requests

response = requests.post(
    "${baseUrl}/api/pipelines",
    headers={"Authorization": "Bearer your_jwt_token"},
    json={
        "name": "Invoice Classifier",
        "schema_ids": ["schema_id_1", "schema_id_2"],
        "min_fields_matched": 2
    }
)
pipeline = response.json()
print(f"Created pipeline: {pipeline['id']}")`,
        javascript: `const response = await fetch("${baseUrl}/api/pipelines", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    name: "Invoice Classifier",
    schema_ids: ["schema_id_1", "schema_id_2"],
    min_fields_matched: 2
  })
});
const pipeline = await response.json();`,
      },
    },

    {
      sectionId: "pipeline",
      id: "ep-pipeline-keys",
      method: "POST",
      path: "/api/pipelines/{pipeline_id}/keys",
      auth: "Bearer JWT",
      title: "Create Pipeline API Key",
      description: "Generate a new API key scoped to a specific pipeline. The full key is returned only once — store it securely. Subsequent requests only return the key prefix.",
      pathParams: [
        { name: "pipeline_id", type: "string", required: true, description: "Pipeline ID to scope this key to" },
      ],
      bodyParams: [
        { name: "name", type: "string", required: false, description: "Friendly name for the key (default: Default Key)" },
      ],
      responseExample: {
        id: "key_uuid",
        pipeline_id: "pl_abc123",
        name: "Production Key",
        key_prefix: "pk_Xj9kL2mN",
        full_key: "pk_Xj9kL2mNaBcDeFgHiJkLmNoPqRsTuVwXyZ...",
        is_active: true,
        created_at: "2024-01-15T10:30:00Z",
        last_used_at: null
      },
      codeExamples: {
        curl: `curl -X POST "${baseUrl}/api/pipelines/{pipeline_id}/keys" \\
  -H "Authorization: Bearer your_jwt_token" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Production Key"}'`,
        python: `import requests

PIPELINE_ID = "your_pipeline_id"
response = requests.post(
    f"${baseUrl}/api/pipelines/{'{PIPELINE_ID}'}/keys",
    headers={"Authorization": "Bearer your_jwt_token"},
    json={"name": "Production Key"}
)
key = response.json()
# Store full_key securely — shown only once
print(f"Full key (save this!): {key['full_key']}")`,
        javascript: `const response = await fetch(
  \`${baseUrl}/api/pipelines/\${pipelineId}/keys\`,
  {
    method: "POST",
    headers: {
      "Authorization": \`Bearer \${token}\`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ name: "Production Key" })
  }
);
const key = await response.json();
// Store full_key securely — shown only once!
console.log("Save this key:", key.full_key);`,
      },
    },

    // ── Documents ────────────────────────────────────────────────────────────
    {
      sectionId: "documents",
      id: "ep-get-document",
      method: "GET",
      path: "/api/documents/{doc_id}",
      auth: "Bearer JWT",
      title: "Get Document",
      description: "Retrieve a document by ID including its extracted fields, OCR text, and current status. This is useful for polling the status of an asynchronously processed document.",
      headers: [
        { name: "Authorization", type: "string", required: true, description: "Bearer {jwt_token}" },
      ],
      pathParams: [
        { name: "doc_id", type: "string", required: true, description: "Document UUID returned from the ingest endpoint" },
      ],
      responseExample: {
        id: "3f1e9b2c-...",
        company_id: "company_uuid",
        schema_id: "sch_invoice",
        original_filename: "invoice.pdf",
        status: "review",
        ocr_pages_count: 2,
        extracted_fields: {
          invoice_number: "INV-2024-001",
          total_amount: "1,250.00"
        },
        created_at: "2024-01-15T10:30:00Z",
        processed_at: "2024-01-15T10:30:05Z"
      },
      codeExamples: {
        curl: `curl -X GET "${baseUrl}/api/documents/{doc_id}" \\
  -H "Authorization: Bearer your_jwt_token"`,
        python: `import requests, time

DOC_ID = "document_uuid_here"
headers = {"Authorization": "Bearer your_jwt_token"}

# Poll until processing is complete
while True:
    res = requests.get(f"${baseUrl}/api/documents/{'{DOC_ID}'}", headers=headers)
    doc = res.json()
    if doc["status"] in ("review", "approved", "error"):
        break
    time.sleep(2)

print(f"Status: {doc['status']}")
print(f"Fields: {doc['extracted_fields']}")`,
        javascript: `async function pollDocument(docId, token) {
  const url = \`${baseUrl}/api/documents/\${docId}\`;
  while (true) {
    const res = await fetch(url, {
      headers: { "Authorization": \`Bearer \${token}\` }
    });
    const doc = await res.json();
    if (["review", "approved", "error"].includes(doc.status)) {
      return doc;
    }
    await new Promise(r => setTimeout(r, 2000));
  }
}

const doc = await pollDocument("doc_id_here", token);
console.log("Fields:", doc.extracted_fields);`,
      },
    },

    {
      sectionId: "documents",
      id: "ep-list-documents",
      method: "GET",
      path: "/api/documents",
      auth: "Bearer JWT",
      title: "List Documents",
      description: "List all documents for your company, with optional filtering by schema and status.",
      headers: [
        { name: "Authorization", type: "string", required: true, description: "Bearer {jwt_token}" },
      ],
      bodyParams: [
        { name: "schema_id", type: "query string", required: false, description: "Filter by schema ID" },
        { name: "status", type: "query string", required: false, description: "Filter by status: processing, review, approved, rejected, error" },
      ],
      codeExamples: {
        curl: `# All documents
curl -X GET "${baseUrl}/api/documents" \\
  -H "Authorization: Bearer your_jwt_token"

# Filtered by status
curl -X GET "${baseUrl}/api/documents?status=approved" \\
  -H "Authorization: Bearer your_jwt_token"`,
        python: `import requests

headers = {"Authorization": "Bearer your_jwt_token"}

# Get only approved documents
res = requests.get(
    "${baseUrl}/api/documents",
    params={"status": "approved"},
    headers=headers
)
docs = res.json()
for doc in docs:
    print(f"{doc['original_filename']} — {doc['status']}")`,
        javascript: `const res = await fetch(\`${baseUrl}/api/documents?status=approved\`, {
  headers: { "Authorization": \`Bearer \${token}\` }
});
const documents = await res.json();`,
      },
    },

    // ── Public Requests ──────────────────────────────────────────────────────
    {
      sectionId: "requests",
      id: "ep-get-request",
      method: "GET",
      path: "/api/public/requests/{token}",
      title: "Get Public Request Info",
      description: "Retrieve metadata for a document request link (Dev or Production). No authentication required — this is the endpoint your upload page calls to load request details.",
      pathParams: [
        { name: "token", type: "string", required: true, description: "Unique token from the document request link" },
      ],
      responseExample: {
        id: "req_uuid",
        title: "Please upload your invoice",
        description: "We need your invoice for processing.",
        status: "pending",
        link_type: "production",
        expires_at: "2024-02-15T00:00:00Z",
        schema: { name: "Invoice Schema", fields: [] }
      },
      codeExamples: {
        curl: `curl -X GET "${baseUrl}/api/public/requests/{token}"`,
        python: `import requests
token = "your_request_token"
res = requests.get(f"${baseUrl}/api/public/requests/{'{token}'}")
info = res.json()
print(f"Title: {info['title']}, Type: {info['link_type']}")`,
        javascript: `const token = "your_request_token";
const res = await fetch(\`${baseUrl}/api/public/requests/\${token}\`);
const info = await res.json();
console.log(info.title, info.link_type);`,
      },
    },
  ];
}

const SECTIONS = [
  {
    id: "auth",
    icon: Lock,
    label: "Authentication",
    description: "Obtain JWT tokens for authenticated API access",
  },
  {
    id: "ingest",
    icon: FileText,
    label: "Direct Ingest",
    description: "Submit documents for OCR and field extraction via company API key",
  },
  {
    id: "pipeline",
    icon: Zap,
    label: "Pipelines",
    description: "Multi-schema extraction with pipeline API keys",
  },
  {
    id: "documents",
    icon: Globe,
    label: "Documents",
    description: "Retrieve and manage processed documents",
  },
  {
    id: "requests",
    icon: Key,
    label: "Public Requests",
    description: "Public upload link management",
  },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ApiDocs() {
  const [activeSection, setActiveSection] = useState("auth");
  const [copiedUrl, copyUrl] = useCopy();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const { appName } = useAppConfig();
  const ENDPOINTS = buildEndpoints(BACKEND_URL);

  // Intersection observer for active section tracking
  useEffect(() => {
    const sectionEls = SECTIONS.map(s => document.getElementById(`section-${s.id}`));
    const obs = new IntersectionObserver(
      entries => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          const id = visible[0].target.id.replace("section-", "");
          setActiveSection(id);
        }
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );
    sectionEls.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex gap-0 min-h-full -m-6 bg-[#F8F8F5]">
      {/* ── Left Nav ────────────────────────────────────────────────────── */}
      <aside className="w-56 flex-shrink-0 sticky top-0 h-screen overflow-y-auto bg-white border-r border-slate-200 flex flex-col">
        <div className="px-4 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-0.5">
            <BookOpen size={14} className="text-[#006080]" />
            <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">API Reference</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-0.5">v1.0</p>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5">
          {SECTIONS.map(section => {
            const SectionIcon = section.icon;
            const isActive = activeSection === section.id;
            const sectionEndpoints = ENDPOINTS.filter(e => e.sectionId === section.id);
            return (
              <div key={section.id}>
                <button
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left ${
                    isActive
                      ? "bg-[#E5F0F2] text-[#006080]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <SectionIcon size={13} className="flex-shrink-0" />
                  <span>{section.label}</span>
                </button>
                {isActive && (
                  <div className="ml-5 mt-0.5 space-y-0.5">
                    {sectionEndpoints.map(ep => (
                      <button
                        key={ep.id}
                        onClick={() => document.getElementById(ep.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                        className="w-full text-left flex items-center gap-2 px-2 py-1 rounded text-[11px] text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <span className={`font-bold text-[9px] uppercase ${METHOD_STYLES[ep.method]?.includes("006080") ? "text-[#006080]" : ep.method === "GET" ? "text-slate-500" : ep.method === "DELETE" ? "text-red-500" : "text-amber-600"}`}>
                          {ep.method}
                        </span>
                        <span className="truncate">{ep.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-200 bg-white">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">{appName} API Reference</h1>
          <p className="text-sm text-slate-500 mb-4">
            Integrate document intelligence into your application. All endpoints accept and return JSON.
          </p>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-950 rounded-lg px-4 py-2.5 flex-1 max-w-xl">
              <span className="text-xs text-slate-400 font-mono">Base URL</span>
              <code className="text-xs font-mono text-[#4DB8CC] flex-1 truncate">{BACKEND_URL}</code>
              <button
                onClick={() => copyUrl(BACKEND_URL)}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors ml-2"
              >
                {copiedUrl ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 border border-slate-200 rounded-lg px-3 py-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>All responses are JSON</span>
            </div>
          </div>
        </div>

        <div className="px-8 py-8 space-y-16">
          {SECTIONS.map(section => {
            const SectionIcon = section.icon;
            const sectionEndpoints = ENDPOINTS.filter(e => e.sectionId === section.id);
            return (
              <section key={section.id} id={`section-${section.id}`}>
                {/* Section header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-[#E5F0F2] rounded-lg flex items-center justify-center">
                    <SectionIcon size={14} className="text-[#006080]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">{section.label}</h2>
                    <p className="text-xs text-slate-500">{section.description}</p>
                  </div>
                </div>

                {/* Endpoints */}
                <div className="space-y-5">
                  {sectionEndpoints.map(ep => (
                    <EndpointCard key={ep.id} ep={ep} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </main>
    </div>
  );
}
